import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {AccountLink, UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {getRestoreUsersResult, getUsersRestoreProgress} from 'src/openapi/admin/apis/BulkUsersRestoreApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';
import {pluralize} from '@pb/kit-formatters';

import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {RestoreUserItem} from 'src/openapi/admin/models/RestoreUserItem';

type Props = {
    id: string,
    onClose: () => void,
};

export const RemoveAttendeesResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const getPaginatedResults = useCallback(
        (bulkResult: RestoreUserItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: RestoreUserItem, index) => {
                const userLink = (
                    <UserLink
                        userId={res.userId || 0}
                        caption={res.email}
                        blank={true}
                        place={`${TOOL_TITLE} Result`}
                    />
                );

                return (
                    <Row key={index}>
                        <Cell width={50}>
                            <RowNumbered
                                maxRowNumber={bulkResult?.length || 0}
                                placeholder={(currentPage - 1) * pageSize + index + 1}
                            />
                        </Cell>
                        <Cell>{userLink}</Cell>
                        <Cell>
                            <CompleteMark errors={res.errors}/>
                        </Cell>
                    </Row>
                );
            }),
        [],
    );

    const getConferenceLink = useCallback((itemList: Array<RestoreUserItem>) => (
        <AccountLink
            blank={true}
            place={`${TOOL_TITLE} Result`}
            accountId={itemList[0].conferenceAccountId}
            accountName={itemList[0].conferenceName}
        />
    ), []);

    return (
        <BulkActionResultModal<RestoreUserItem>
            id={id}
            onClose={onClose}
            width={1200}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between">
                    {bulkResult && bulkResult.itemList && bulkResult.itemList.length > 0 ? (
                        <div>
                            Result of the removal of {
                                pluralize('user[s]', bulkResult?.itemList?.length)
                            } from the {getConferenceLink(bulkResult.itemList)}
                        </div>
                    ) : null}
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult?.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            userId: item.userId.toString(),
                            email: item.email,
                            conferenceAccountId: item.conferenceAccountId.toString(),
                            conferenceName: item.conferenceName,
                            errors: item.errors?.join('. '),
                            actionStatus: item.actionStatus,
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(result, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={56}><strong>#</strong></Cell>
                        <Cell>Account</Cell>
                        <Cell>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(result, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getUsersRestoreProgress({id}))}
            getResult={() => apiDispatch(getRestoreUsersResult({id}))}

            sortComparator={(a, b) => a.email.localeCompare(b.email)}
        />
    );
};
